import React from 'react';
import Box from '@mui/material/Box';
import { useTheme, useMediaQuery } from '@mui/material';

/**
 * FormActions — bottom action bar for forms.
 *
 * Desktop: rendered as a normal flex row, right-aligned, with top padding.
 *
 * Mobile (< md): `position: sticky; bottom: 0` so the Save/Cancel buttons
 * stay anchored at the bottom of the viewport as the user scrolls the form.
 * Includes safe-area-inset-bottom clearance for iOS home-bar devices and a
 * subtle top shadow to separate it from the scrolling content above.
 *
 * IMPORTANT: do NOT put FormActions inside a container with overflow: hidden —
 * sticky positioning requires the scroll container to be an ancestor, not a
 * sibling. FormActions must be a direct child of the natural-scroll
 * PageContent (i.e. <PageContent> without the `fill` prop).
 *
 * Usage:
 *   <FormActions>
 *     <Button onClick={onCancel}>Cancel</Button>
 *     <Button variant="contained" type="submit">Save</Button>
 *   </FormActions>
 *
 * Props:
 *   children  {node}    — action buttons, rendered left → right
 *   sx        {object}  — extra sx overrides
 */
export default function FormActions({ children, sx, ...rest }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        gap: 1,
        mt: 2,
        pt: 2,
        alignItems: 'center',

        ...(isMobile && {
          position: 'sticky',
          bottom: 0,
          // Safe-area clearance for iOS home bar
          pb: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`,
          px: 2,
          // Slight background + shadow to visually lift above scrolling content
          backgroundColor: theme.palette.background.paper,
          boxShadow: `0 -2px 8px ${
            theme.palette.mode === 'dark'
              ? 'rgba(0,0,0,0.4)'
              : 'rgba(0,0,0,0.08)'
          }`,
          // Stretch full bleed so the bg covers the full width
          mx: -2,
          zIndex: 1,
        }),

        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}
